/**
 * +specs: the Warrior's three specialisations (docs/design/classes/specs.md "Warrior"). The reference
 * implementation for the other classes' agents: every base key 1–7 evolves on the same key, the
 * signature mechanic lives in passives / auras, spec-only abilities sit on Q (and E).
 *
 *  - Ironclad (tank): Plating, 0–5 iron plates from blocks and builders; armour and block per plate,
 *    Unstoppable at 5; Shrapnel Burst cashes them as AoE damage and healing.
 *  - Ravager (melee dps): two greatswords; Bloodheat feeds Twin Reaping, which keeps Overdrive up and
 *    stacks Carnage while it holds.
 *  - Bloodreaver (melee dps): Thirst from the health it loses (on purpose: Bleeding Edge, Rending Arc,
 *    Bloodletting); missing health turns into crit and haste; Bloodwrench drinks it all back.
 *
 * Names: original (checked against WoW's spell list; content specs.test.ts holds the denylist).
 * Numbers: tuned with `pnpm harness -- specs` (docs/design/classes/specs.md "Balance").
 */
import type { AbilityDef, AuraDef, Condition, EffectSpec, SpecDef } from '@keystone/sim';
import { ABILITY, BASE_ABILITY, BASE_AURA } from '../../ids';
import { PLAYER_HP } from '../../balance';
import { OFF_GCD, aroundSelf, aroundTarget, interrupt } from '../common';
import { type SpecInfo, infoOf } from './types';

const WA = BASE_ABILITY.warrior;
const W = ABILITY.warden;
const B = ABILITY.blademaster;

export const WARRIOR_SPEC = { ironclad: 'ironclad', ravager: 'ravager', bloodreaver: 'bloodreaver' } as const;

/** Ability ids (key in the comment). */
export const IRONCLAD = {
  rivetStrike: 'ironclad.rivet_strike', // 1 (Breaker)
  anchorChain: 'ironclad.anchor_chain', // 2 (Headlong)
  shieldSweep: 'ironclad.shield_sweep', // 3 (Wide Arc)
  shrapnelBurst: 'ironclad.shrapnel_burst', // 4 (Rive)
  wreckage: 'ironclad.wreckage', // 5 (Bloodprize)
  platedBash: 'ironclad.plated_bash', // 6 (Shield Check)
  plateAegis: 'ironclad.plate_aegis', // 7 (Iron Breath)
  ironMaiden: 'ironclad.iron_maiden', // Q
} as const;
export const IRONCLAD_AURA = {
  plating: 'ironclad.plating',
  unstoppable: 'ironclad.unstoppable',
  braced: 'ironclad.braced',
  anchored: 'ironclad.anchored',
  shrapnel: 'ironclad.shrapnel_bleed',
  plateAegis: 'ironclad.plate_aegis_absorb',
  plateAegisDr: 'ironclad.plate_aegis_dr',
  ironMaiden: 'ironclad.iron_maiden_buff',
  platesmith: 'ironclad.platesmith', // hidden passive: blocks and kicks bolt on plates
} as const;

export const RAVAGER = {
  twinReaping: 'ravager.twin_reaping', // 1 (Breaker)
  breakneck: 'ravager.breakneck', // 2 (Headlong)
  steelCyclone: 'ravager.steel_cyclone', // 3 (Wide Arc)
  redHunger: 'ravager.red_hunger', // 4 (Rive)
  headsman: 'ravager.headsman', // 5 (Bloodprize)
  hiltCrack: 'ravager.hilt_crack', // 6 (Shield Check)
  wrathblood: 'ravager.wrathblood', // 7 (Iron Breath)
  unchained: 'ravager.unchained', // Q
} as const;
export const RAVAGER_AURA = {
  overdrive: 'ravager.overdrive',
  carnage: 'ravager.carnage',
  cycloneEdge: 'ravager.cyclone_edge',
  unchained: 'ravager.unchained_buff',
  breakneckRoot: 'ravager.breakneck_root',
  wrathblood: 'ravager.wrathblood_dr',
} as const;

export const BLOODREAVER = {
  bloodwrench: 'bloodreaver.bloodwrench', // 1 (Breaker)
  gorecharge: 'bloodreaver.gorecharge', // 2 (Headlong)
  rendingArc: 'bloodreaver.rending_arc', // 3 (Wide Arc)
  bleedingEdge: 'bloodreaver.bleeding_edge', // 4 (Rive)
  finalDraught: 'bloodreaver.final_draught', // 5 (Bloodprize)
  crimsonKick: 'bloodreaver.crimson_kick', // 6 (Shield Check)
  sanguineVigor: 'bloodreaver.sanguine_vigor', // 7 (Iron Breath)
  bloodletting: 'bloodreaver.bloodletting', // Q
  crimsonTrance: 'bloodreaver.crimson_trance', // E
} as const;
export const BLOODREAVER_AURA = {
  redThirst: 'bloodreaver.red_thirst', // passive: missing health → crit, haste; leech
  weepingWound: 'bloodreaver.weeping_wound',
  openVein: 'bloodreaver.open_vein',
  crimsonTrance: 'bloodreaver.crimson_trance_buff',
  sanguineVigor: 'bloodreaver.sanguine_vigor_dr',
  goreRoot: 'bloodreaver.gorecharge_root',
} as const;

const FRESH_KILL = BASE_AURA.freshKill;
const hasFreshKill: Condition = { type: 'selfHasAura', auraId: FRESH_KILL };
/** Dps Warriors drop the shield: no block, a little less armour, Warrior melee health. */
const DPS_STATS = { armor: 0.2, dodge: 0.05, parry: 0.08, block: 0, blockAmount: 0.3 };
const root = (id: string, name: string, ms: number): AuraDef => ({ id, name, kind: 'debuff', durationMs: ms, stacking: 'refresh', cc: 'root' });
const dr = (id: string, name: string, ms: number, v: number): EffectSpec => ({ type: 'applyAura', to: 'self', aura: { id, name, kind: 'buff', durationMs: ms, stacking: 'refresh', mods: [{ stat: 'damageTakenPct', value: v }] } });

// ============================================================================
// Ironclad (tank): Plating
// ============================================================================

const unstoppable: AuraDef = {
  id: IRONCLAD_AURA.unstoppable,
  name: 'Unstoppable',
  kind: 'buff',
  durationMs: 0,
  stacking: 'refresh',
  mods: [{ stat: 'damageDonePct', value: 0.1 }],
  flags: { immuneSlow: true, immuneDisplacement: true },
  ui: { proc: true, glow: { abilityIds: [IRONCLAD.shrapnelBurst] } },
};

/** The signature: 0–5 iron plates. Each adds armour and block; at 5 you are Unstoppable. */
const plating: AuraDef = {
  id: IRONCLAD_AURA.plating,
  name: 'Plating',
  kind: 'buff',
  durationMs: 20_000,
  stacking: 'stack',
  maxStacks: 5,
  mods: [
    { stat: 'armor', value: 0.04 },
    { stat: 'block', value: 0.05 },
  ],
  onRemove: [{ type: 'removeAura', to: 'self', auraId: IRONCLAD_AURA.unstoppable }],
  ui: { stackCounterOn: [IRONCLAD.shrapnelBurst, IRONCLAD.plateAegis], glow: { abilityIds: [IRONCLAD.shrapnelBurst], atStacks: 5 } },
};

/** Bolt `n` plates on (each refreshes the 20 s timer); reaching 5 makes you Unstoppable. */
const plates = (n: number): EffectSpec[] => [
  { type: 'applyAura', to: 'self', aura: plating, stacks: n },
  { type: 'conditional', cond: { type: 'selfHasAura', auraId: IRONCLAD_AURA.plating, minStacks: 5 }, then: [{ type: 'applyAura', to: 'self', aura: unstoppable }] },
];

const braced: AuraDef = {
  id: IRONCLAD_AURA.braced,
  name: 'Braced',
  kind: 'buff',
  durationMs: 6000,
  stacking: 'refresh',
  mods: [{ stat: 'block', value: 1 }],
  procs: [{ on: 'avoided', hitTypes: ['block'], effects: [{ type: 'removeAura', to: 'self', auraId: IRONCLAD_AURA.braced }] }],
};

const shrapnelBleed: AuraDef = {
  id: IRONCLAD_AURA.shrapnel,
  name: 'Shrapnel',
  kind: 'debuff',
  durationMs: 9000,
  stacking: 'refresh',
  pandemic: true,
  periodic: { intervalMs: 3000, effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.45 }, avoidable: false, threatMult: 2 }] },
  mods: [{ stat: 'moveSpeedPct', value: -0.3 }],
};

const ironMaidenBuff: AuraDef = {
  id: IRONCLAD_AURA.ironMaiden,
  name: 'Iron Maiden',
  kind: 'buff',
  durationMs: 8000,
  stacking: 'refresh',
  mods: [{ stat: 'damageTakenPct', value: -0.2 }],
  // Every hit you take sprays shrapnel back at the attacker.
  procs: [{ on: 'damageTaken', icdMs: 400, effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.9, variance: 0.05 }, avoidable: false, threatMult: 2 }] }],
  ui: { proc: true, glow: { abilityIds: [IRONCLAD.shrapnelBurst] } },
};

/** Hidden passive: every block bolts on a plate (1 s internal cooldown); a landed kick bolts one on too. */
const platesmith: AuraDef = {
  id: IRONCLAD_AURA.platesmith,
  name: 'Platesmith',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { hidden: true, persistThroughDeath: true },
  procs: [
    { on: 'avoided', hitTypes: ['block'], icdMs: 1000, effects: plates(1) },
    { on: 'interruptDealt', abilityIds: [IRONCLAD.platedBash], effects: plates(1) },
  ],
};

const rivetStrike: AbilityDef = {
  id: IRONCLAD.rivetStrike,
  name: 'Rivet Strike',
  school: 'physical',
  description: 'Spend 15 Grit: hammer the target and rivet a plate onto your armour.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cost: [{ resource: 'grit', amount: 15 }],
  flatThreat: 1400,
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 3.3, variance: 0.05 } }, ...plates(1)],
  ui: {
    tags: ['spender'],
    color: '#7f8fa6',
    animAs: W.bulwarkStrike,
    desc: 'Spend 15 Grit on a hammering blow for {damage} Physical damage and gain 1 Plate. Grit builds from your swings and the hits you take.',
    hint: 'Press 1 every two swings: each Rivet Strike adds a plate.',
  },
};

const anchorChain: AbilityDef = {
  id: IRONCLAD.anchorChain,
  name: 'Anchor Chain',
  school: 'physical',
  description: 'Hook an enemy up to 25 yd away and drag it to your shield. Roots it for 1 s, +15 Grit and 1 Plate.',
  targeting: 'enemy',
  range: 25,
  requiresFacing: true,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 12_000 },
  gain: [{ resource: 'grit', amount: 15 }],
  flatThreat: 900,
  effects: [
    { type: 'grip', stopAt: 2 },
    { type: 'damage', school: 'physical', amount: { coef: 0.8, variance: 0.05 }, avoidable: false },
    { type: 'applyAura', aura: root(IRONCLAD_AURA.anchored, 'Anchored', 1000) },
    ...plates(1),
  ],
  ui: {
    tags: ['movement'],
    color: '#8a9bb4',
    animAs: W.grip,
    desc: 'Hook an enemy up to 25 yd away and drag it to your shield for {damage} Physical damage. It is rooted for 1 sec; you gain 15 Grit and 1 Plate. Off the global cooldown.',
    hint: 'Press 2 to drag a caster or a runner to you.',
  },
};

const shieldSweep: AbilityDef = {
  id: IRONCLAD.shieldSweep,
  name: 'Shield Sweep',
  school: 'physical',
  description: 'Sweep your shield through every enemy within 8 yd. Double threat, +12 Grit, and your next hit taken is blocked.',
  targeting: 'none',
  cooldown: { durationMs: 5000 },
  gain: [{ resource: 'grit', amount: 12 }],
  flatThreat: 700,
  effects: [
    { type: 'damage', school: 'physical', to: 'area', area: aroundSelf(8, 10), amount: { coef: 2.4, variance: 0.05 }, threatMult: 2, isAoE: true },
    { type: 'applyAura', to: 'self', aura: braced },
  ],
  ui: {
    tags: ['aoe'],
    color: '#9fb4d8',
    animAs: W.cleaveStrike,
    desc: 'Sweep your shield through every enemy within 8 yd for {damage} Physical damage with double threat. Generates 12 Grit and Braces you: the next attack that hits you within 6 sec is blocked (and blocks add Plates).',
    hint: 'Press 3 on cooldown: hits the pack and guarantees your next block.',
  },
};

const shrapnelBurst: AbilityDef = {
  id: IRONCLAD.shrapnelBurst,
  name: 'Shrapnel Burst',
  school: 'physical',
  description: 'Blast every plate outward: damage and a bleed on every enemy within 8 yd, and heal yourself, more per plate.',
  targeting: 'none',
  usableIf: { type: 'selfHasAura', auraId: IRONCLAD_AURA.plating },
  flatThreat: 900,
  effects: [
    { type: 'damage', school: 'physical', to: 'area', area: aroundSelf(8, 10), amount: { coef: 1.0, variance: 0.05, perStack: { auraId: IRONCLAD_AURA.plating, on: 'self', mult: 0.8 } }, threatMult: 2, isAoE: true },
    { type: 'applyAura', to: 'area', area: aroundSelf(8, 10), aura: shrapnelBleed },
    { type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.012, perStack: { auraId: IRONCLAD_AURA.plating, on: 'self', mult: 1 } } },
    // Iron Maiden locks the plates in place: the burst fires without spending them.
    { type: 'removeAura', to: 'self', auraId: IRONCLAD_AURA.plating, if: { type: 'selfLacksAura', auraId: IRONCLAD_AURA.ironMaiden } },
  ],
  ui: {
    tags: ['spender', 'aoe'],
    color: '#c7d1e0',
    animAs: W.consecratedGround,
    desc: 'Needs at least 1 Plate. Blast every plate outward: {damage} Physical damage to every enemy within 8 yd (+80% per plate) with double threat, a 9 sec Shrapnel bleed that slows by 30%, and heal 1.2% of your maximum health (+1.2% per plate). Spends all Plates, unless Iron Maiden holds them.',
    hint: 'Press 4 at 5 plates: a big blast that heals you, then build them again.',
  },
};

const wreckage: AbilityDef = {
  id: IRONCLAD.wreckage,
  name: 'Wreckage',
  school: 'physical',
  description: 'Usable for 20 s after a kill: a heavy strike that heals 12% of your maximum health and bolts on 2 plates.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  usableIf: hasFreshKill,
  flatThreat: 800,
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 4.0, variance: 0.05 } },
    { type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.12 } },
    ...plates(2),
    { type: 'removeAura', to: 'self', auraId: FRESH_KILL },
  ],
  ui: {
    tags: ['heal'],
    color: '#a0a8b8',
    animAs: W.shieldSlam,
    desc: 'Usable for 20 sec after an enemy you fought dies. Strike for {damage} Physical damage, heal 12% of your maximum health and gain 2 Plates from the wreckage.',
    hint: 'After a kill key 5 lights up: heal and bolt on 2 plates.',
  },
};

const platedBash: AbilityDef = {
  id: IRONCLAD.platedBash,
  name: 'Plated Bash',
  school: 'physical',
  description: 'Bash the target with your plated shield, interrupting its cast and locking that school for 3 s. A landed interrupt adds a plate.',
  targeting: 'enemy',
  melee: true,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.6 } }, interrupt(3000)],
  ui: { tags: ['interrupt'], color: '#6d8fd6', animAs: W.shieldBash, hint: 'Press 6 on a caster: stops the spell and bolts on a plate.' },
};

const plateAegis: AbilityDef = {
  id: IRONCLAD.plateAegis,
  name: 'Plate Aegis',
  school: 'physical',
  description: 'Brace behind your plates: an absorb that grows with each plate, and 15% less damage taken for 6 s.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 40_000 },
  effects: [
    { type: 'absorb', to: 'self', auraId: IRONCLAD_AURA.plateAegis, amount: { pctSourceMaxHp: 0.06, perStack: { auraId: IRONCLAD_AURA.plating, on: 'self', mult: 1 } } },
    dr(IRONCLAD_AURA.plateAegisDr, 'Plate Aegis', 6000, -0.15),
  ],
  ui: {
    tags: ['defensive', 'majorDefensive'],
    color: '#9fb4d8',
    animAs: W.lastStand,
    desc: 'Absorb {absorb} damage (6% of your maximum health, +6% per Plate) for 10 sec and take 15% less damage for 6 sec. Off the global cooldown.',
    hint: 'Press 7 at high plates before a big hit.',
  },
};

const ironMaiden: AbilityDef = {
  id: IRONCLAD.ironMaiden,
  name: 'Iron Maiden',
  school: 'physical',
  description: 'Lock every plate in place for 8 s: +2 plates, 20% less damage taken, every hit you take sprays shrapnel back, and Shrapnel Burst spends nothing.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 60_000 },
  effects: [...plates(2), { type: 'applyAura', to: 'self', aura: ironMaidenBuff }],
  ui: {
    tags: ['cooldown', 'defensive'],
    color: '#b8c4d6',
    animAs: W.shieldWallBash,
    desc: 'Gain 2 Plates and lock them in place for 8 sec: take 20% less damage, every hit you take sprays shrapnel back at the attacker, and Shrapnel Burst spends no plates. Off the global cooldown.',
    hint: 'Press Q in a big pull, then Shrapnel Burst again and again.',
  },
};

export const ironclad: SpecDef = {
  id: WARRIOR_SPEC.ironclad,
  baseClassId: 'warrior',
  name: 'Ironclad',
  role: 'tank',
  unlockLevel: 10,
  evolutions: {
    [WA.breaker]: IRONCLAD.rivetStrike,
    [WA.headlong]: IRONCLAD.anchorChain,
    [WA.wideArc]: IRONCLAD.shieldSweep,
    [WA.rive]: IRONCLAD.shrapnelBurst,
    [WA.bloodprize]: IRONCLAD.wreckage,
    [WA.shieldCheck]: IRONCLAD.platedBash,
    [WA.ironBreath]: IRONCLAD.plateAegis,
  },
  extraAbilities: [{ key: 'Q', abilityId: IRONCLAD.ironMaiden }],
  abilities: [rivetStrike, anchorChain, shieldSweep, shrapnelBurst, wreckage, platedBash, plateAegis, ironMaiden],
  passives: [platesmith],
  auras: [plating, unstoppable, { id: IRONCLAD_AURA.plateAegis, name: 'Plate Aegis', kind: 'buff', durationMs: 10_000, stacking: 'refresh', absorb: {} }],
  // A tank: the base Warrior's shield, stats and Steadfast Vow (threat) carry over unchanged. No move-speed penalty.
  color: '#8a9bb4',
  ui: {
    tagline: 'A walking fortress that turns its own armour into shrapnel.',
    fantasy: 'Every blow you block bolts another iron plate onto you. You get tougher with every hit, then blast the plates outward in a storm of shrapnel and start building again.',
    signature: 'Plating (0–5): each plate adds 4% armour and 5% block; at 5 you are Unstoppable. Shrapnel Burst spends them all.',
    weapon: 'tower_shield',
    tint: '#9aa7ba',
    glow: '#d9e4f5',
  },
};

// ============================================================================
// Ravager (melee dps): Bloodheat, Overdrive, Carnage
// ============================================================================

const carnage: AuraDef = {
  id: RAVAGER_AURA.carnage,
  name: 'Carnage',
  kind: 'buff',
  durationMs: 0,
  stacking: 'stack',
  maxStacks: 5,
  mods: [{ stat: 'damageDonePct', value: 0.04 }],
  ui: { stackCounterOn: [RAVAGER.twinReaping] },
};

/** Overdrive: the state the spec lives in. Twin Reaping keeps it up; dropping it clears Carnage. */
const overdrive: AuraDef = {
  id: RAVAGER_AURA.overdrive,
  name: 'Overdrive',
  kind: 'buff',
  durationMs: 8000,
  stacking: 'refresh',
  mods: [
    { stat: 'hastePct', value: 0.2 },
    { stat: 'damageDonePct', value: 0.13 }, // +specs balance: 8% → 13% (the Ravager sat at −5% of the dps mean)
  ],
  onRemove: [{ type: 'removeAura', to: 'self', auraId: RAVAGER_AURA.carnage }],
  ui: { proc: true },
};

const cycloneEdge: AuraDef = {
  id: RAVAGER_AURA.cycloneEdge,
  name: 'Cyclone Edge',
  kind: 'buff',
  durationMs: 12_000,
  stacking: 'stack',
  maxStacks: 2,
  ui: { proc: true, glow: { abilityIds: [RAVAGER.twinReaping] }, stackCounterOn: [RAVAGER.twinReaping] },
};

const unchainedBuff: AuraDef = {
  id: RAVAGER_AURA.unchained,
  name: 'Unchained',
  kind: 'buff',
  durationMs: 12_000,
  stacking: 'refresh',
  mods: [{ stat: 'damageDonePct', value: 0.12 }],
  ui: { proc: true },
};

const cleaving: Condition = { type: 'or', any: [{ type: 'selfHasAura', auraId: RAVAGER_AURA.cycloneEdge }, { type: 'selfHasAura', auraId: RAVAGER_AURA.unchained }] };
const reapHit = (coef: number): EffectSpec => ({ type: 'damage', school: 'physical', amount: { coef, variance: 0.05 } });
const reapCleave = (coef: number): EffectSpec => ({ type: 'damage', school: 'physical', to: 'area', area: aroundTarget(8, 6), amount: { coef, variance: 0.05 }, isAoE: true });

const twinReaping: AbilityDef = {
  id: RAVAGER.twinReaping,
  name: 'Twin Reaping',
  school: 'physical',
  description: 'Spend 50 Bloodheat: five furious hits across both greatswords. Triggers Overdrive; while Overdriven, also stacks Carnage.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cost: [{ resource: 'bloodheat', amount: 50 }],
  effects: [
    { type: 'conditional', cond: { type: 'selfHasAura', auraId: RAVAGER_AURA.overdrive }, then: [{ type: 'applyAura', to: 'self', aura: carnage }] },
    { type: 'conditional', cond: cleaving, then: [reapCleave(0.7), reapCleave(0.7), reapCleave(0.7), reapCleave(0.7), reapCleave(0.85)], else: [reapHit(1.2), reapHit(1.2), reapHit(1.2), reapHit(1.2), reapHit(1.5)] },
    { type: 'applyAura', to: 'self', aura: overdrive },
    { type: 'removeAura', to: 'self', auraId: RAVAGER_AURA.cycloneEdge, stacks: 1 },
  ],
  ui: {
    tags: ['spender'],
    color: '#d0473a',
    animAs: B.ravagersFinale,
    desc: 'Spend 50 Bloodheat: five hits across both greatswords for {damage} Physical damage each (the last one harder). Triggers Overdrive (+20% haste, +13% damage, 8 sec). Cast while Overdriven, it also adds a Carnage stack (+4% damage, up to 5) that lasts until Overdrive ends. With Cyclone Edge or Unchained, every hit cleaves enemies within 8 yd of the target.',
    hint: 'Press 1 at 50 Bloodheat, and never let Overdrive drop.',
  },
};

const breakneck: AbilityDef = {
  id: RAVAGER.breakneck,
  name: 'Breakneck',
  school: 'physical',
  description: 'Charge an enemy up to 25 yd away, root it for 1 s and gain 25 Bloodheat.',
  targeting: 'enemy',
  range: 25,
  requiresFacing: true,
  requiresMobility: true,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  gain: [{ resource: 'bloodheat', amount: 25 }],
  effects: [
    { type: 'dash', mode: 'toTarget', distance: 25, speed: 46 },
    { type: 'damage', school: 'physical', amount: { coef: 1.0, variance: 0.05 } },
    { type: 'applyAura', aura: root(RAVAGER_AURA.breakneckRoot, 'Bowled Over', 1000) },
  ],
  ui: { tags: ['movement'], color: '#e0745a', animAs: B.charge, desc: 'Charge an enemy up to 25 yd away for {damage} Physical damage. It is rooted for 1 sec and you gain 25 Bloodheat. Off the global cooldown.', hint: 'Press 2 to open: 25 Bloodheat on arrival.' },
};

const steelCyclone: AbilityDef = {
  id: RAVAGER.steelCyclone,
  name: 'Steel Cyclone',
  school: 'physical',
  description: 'Spin both greatswords through every enemy within 8 yd. +8 Bloodheat, and your next 2 Twin Reapings cleave.',
  targeting: 'none',
  gain: [{ resource: 'bloodheat', amount: 8 }],
  effects: [
    { type: 'damage', school: 'physical', to: 'area', area: aroundSelf(8, 10), amount: { coef: 1.1, variance: 0.05 }, isAoE: true },
    { type: 'conditional', cond: { type: 'enemiesNearSelf', radius: 8, atLeast: 2 }, then: [{ type: 'applyAura', to: 'self', aura: cycloneEdge, stacks: 2 }] },
  ],
  ui: { tags: ['aoe', 'builder'], color: '#c9d3e6', animAs: B.cleave, desc: 'Spin through every enemy within 8 yd for {damage} Physical damage. Generates 8 Bloodheat. With 2 or more enemies around you, your next 2 Twin Reapings cleave (Cyclone Edge).', hint: 'Press 3 as the filler; in a pack, before Twin Reaping.' },
};

const redHunger: AbilityDef = {
  id: RAVAGER.redHunger,
  name: 'Red Hunger',
  school: 'physical',
  description: 'A quick double cut. +20 Bloodheat, heals 2% of your maximum health, and may trigger Overdrive.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cooldown: { durationMs: 4500, hasteScaled: true },
  gain: [{ resource: 'bloodheat', amount: 20 }],
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 1.3, variance: 0.05 } },
    { type: 'damage', school: 'physical', amount: { coef: 1.3, variance: 0.05 } },
    { type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.02 } },
    { type: 'conditional', cond: { type: 'chance', p: 0.3 }, then: [{ type: 'applyAura', to: 'self', aura: overdrive }] },
  ],
  ui: { tags: ['builder'], color: '#b8323c', animAs: B.overpower, desc: 'Cut twice for {damage} Physical damage each. Generates 20 Bloodheat, heals you for 2% of your maximum health, and has a 30% chance to trigger Overdrive.', hint: 'Press 4 on cooldown: your main Bloodheat builder.' },
};

const headsman: AbilityDef = {
  id: RAVAGER.headsman,
  name: 'Headsman',
  school: 'physical',
  description: 'Usable on an enemy below 35% health, or for 20 s after a kill: a crossed beheading blow. After a kill it also heals 10%.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  usableIf: { type: 'or', any: [hasFreshKill, { type: 'targetHpBelow', pct: 0.35 }] },
  gain: [{ resource: 'bloodheat', amount: 10 }],
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 3.6, variance: 0.05 } },
    { type: 'conditional', cond: hasFreshKill, then: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.1 } }, { type: 'removeAura', to: 'self', auraId: FRESH_KILL }] },
  ],
  ui: { tags: ['execute'], color: '#e04848', animAs: B.execute, desc: 'Usable on an enemy below 35% health, or for 20 sec after an enemy you fought dies. Cross both blades for {damage} Physical damage and gain 10 Bloodheat; after a kill it also heals 10% of your maximum health.', hint: 'Key 5 lights up below 35% and after kills: press it.' },
};

const hiltCrack: AbilityDef = {
  id: RAVAGER.hiltCrack,
  name: 'Hilt Crack',
  school: 'physical',
  description: 'Crack the target with a pommel, interrupting its cast and locking that school for 3 s. +10 Bloodheat.',
  targeting: 'enemy',
  melee: true,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  gain: [{ resource: 'bloodheat', amount: 10 }],
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.6 } }, interrupt(3000)],
  ui: { tags: ['interrupt'], color: '#d9a066', animAs: B.pummel, hint: 'Press 6 while an enemy is casting.' },
};

const wrathblood: AbilityDef = {
  id: RAVAGER.wrathblood,
  name: 'Wrathblood',
  school: 'physical',
  description: 'Burn the rage into blood: heal 20% of your maximum health (30% while Overdriven) and take 20% less damage for 6 s.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 40_000 },
  effects: [
    { type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.2 } },
    { type: 'conditional', cond: { type: 'selfHasAura', auraId: RAVAGER_AURA.overdrive }, then: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.1 } }] },
    dr(RAVAGER_AURA.wrathblood, 'Wrathblood', 6000, -0.2),
  ],
  ui: { tags: ['defensive', 'majorDefensive'], color: '#c0392b', animAs: B.dieByTheSword, hint: 'Press 7 when low; it heals more while Overdriven.' },
};

const unchained: AbilityDef = {
  id: RAVAGER.unchained,
  name: 'Unchained',
  school: 'physical',
  description: 'Overdrive for 12 s and +30 Bloodheat; for 12 s you deal 12% more damage and every Twin Reaping cleaves.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 60_000 },
  gain: [{ resource: 'bloodheat', amount: 30 }],
  effects: [
    { type: 'applyAura', to: 'self', aura: overdrive, durationMs: 12_000 },
    { type: 'applyAura', to: 'self', aura: unchainedBuff },
  ],
  ui: { tags: ['cooldown'], color: '#ff6a3d', animAs: B.bladestorm, desc: 'Enter Overdrive for 12 sec and gain 30 Bloodheat. For 12 sec you deal 12% more damage and every Twin Reaping cleaves the pack. Off the global cooldown.', hint: 'Press Q at the start of a pull or a boss.' },
};

export const ravager: SpecDef = {
  id: WARRIOR_SPEC.ravager,
  baseClassId: 'warrior',
  name: 'Ravager',
  role: 'dps',
  unlockLevel: 10,
  evolutions: {
    [WA.breaker]: RAVAGER.twinReaping,
    [WA.headlong]: RAVAGER.breakneck,
    [WA.wideArc]: RAVAGER.steelCyclone,
    [WA.rive]: RAVAGER.redHunger,
    [WA.bloodprize]: RAVAGER.headsman,
    [WA.shieldCheck]: RAVAGER.hiltCrack,
    [WA.ironBreath]: RAVAGER.wrathblood,
  },
  extraAbilities: [{ key: 'Q', abilityId: RAVAGER.unchained }],
  abilities: [twinReaping, breakneck, steelCyclone, redHunger, headsman, hiltCrack, wrathblood, unchained],
  // Bloodheat: from every landed swing of either blade and from hits taken; cools off out of combat.
  resources: [{ id: 'bloodheat', name: 'Bloodheat', max: 100, start: 0, regenPerSecOoc: -10, oocSettle: 0, gainOn: [{ on: 'meleeSwingLanded', amount: 5 }, { on: 'damageTaken', amount: 1, perPctMaxHp: true }] }],
  removePassives: [BASE_AURA.warriorThreat],
  auras: [overdrive, carnage, cycloneEdge, unchainedBuff],
  baseStats: DPS_STATS,
  maxHp: PLAYER_HP.melee,
  // Two greatswords: a full-power main hand and a second blade half a beat behind.
  melee: { damage: { coef: 1.5, variance: 0.05 }, speedMs: 3000, school: 'physical', offhand: { speedMs: 3000, mult: 0.8 } },
  color: '#d0473a',
  ui: {
    tagline: 'A two-greatsword berserker that lives inside an endless Overdrive.',
    fantasy: 'One two-handed sword is not enough, so you swing two. Bloodheat pours in from every hit you deal and take, and the whole spec is about staying Overdriven: faster, louder, bigger numbers, nonstop.',
    signature: 'Overdrive: Twin Reaping (50 Bloodheat) grants +20% haste and +13% damage for 8 s; every Twin Reaping inside it adds a Carnage stack (+4%, up to 5) that lasts until Overdrive drops.',
    weapon: 'twin_greatswords',
    tint: '#d0473a',
    glow: '#ff8a5c',
  },
};

// ============================================================================
// Bloodreaver (melee dps): Thirst
// ============================================================================

/** The signature passive: missing health → crit and haste, and a sip of everything you cut. */
const redThirst: AuraDef = {
  id: BLOODREAVER_AURA.redThirst,
  name: 'Red Thirst',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  mods: [
    { stat: 'critPct', value: 0.5, perMissingHp: true },
    { stat: 'hastePct', value: 0.3, perMissingHp: true },
    { stat: 'leechPct', value: 0.03 },
  ],
  flags: { persistThroughDeath: true },
};

const weepingWound: AuraDef = {
  id: BLOODREAVER_AURA.weepingWound,
  name: 'Weeping Wound',
  kind: 'debuff',
  durationMs: 12_000,
  stacking: 'stack',
  maxStacks: 3,
  periodic: { intervalMs: 2000, effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.3 }, avoidable: false }] },
  ui: { glow: { abilityIds: [BLOODREAVER.bloodwrench], atStacks: 3 }, stackCounterOn: [BLOODREAVER.bloodwrench] },
};

const openVein: AuraDef = {
  id: BLOODREAVER_AURA.openVein,
  name: 'Open Vein',
  kind: 'buff',
  durationMs: 10_000,
  stacking: 'refresh',
  mods: [{ stat: 'damageDonePct', value: 0.3, abilityIds: [BLOODREAVER.bloodwrench] }],
  procs: [{ on: 'castResolved', abilityIds: [BLOODREAVER.bloodwrench], consumeStack: true, effects: [] }],
  ui: { proc: true, glow: { abilityIds: [BLOODREAVER.bloodwrench] } },
};

const crimsonTranceBuff: AuraDef = {
  id: BLOODREAVER_AURA.crimsonTrance,
  name: 'Crimson Trance',
  kind: 'buff',
  durationMs: 10_000,
  stacking: 'refresh',
  mods: [
    { stat: 'leechPct', value: 0.2 },
    { stat: 'damageDonePct', value: 0.1 },
  ],
  flags: { cannotDie: true },
  ui: { proc: true },
};

/** Lose `pct` of your maximum health on purpose (never lethal; Thirst comes from the loss). */
const bleedSelf = (pct: number): EffectSpec => ({ type: 'damage', to: 'self', school: 'physical', amount: { pctSourceMaxHp: pct }, selfHarm: true });
const wound = (stacks = 1): EffectSpec => ({ type: 'applyAura', aura: weepingWound, stacks });

const bloodwrench: AbilityDef = {
  id: BLOODREAVER.bloodwrench,
  name: 'Bloodwrench',
  school: 'physical',
  description: 'Spend 50 Thirst: tear out every Weeping Wound on the target for big damage, and drink it: heal for a share of your missing health.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cost: [{ resource: 'thirst', amount: 50 }],
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 3.2, variance: 0.05, perStack: { auraId: BLOODREAVER_AURA.weepingWound, on: 'target', mult: 0.45 } } },
    { type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.02, pctSourceMissingHp: 0.2 } },
    { type: 'removeAura', auraId: BLOODREAVER_AURA.weepingWound },
  ],
  ui: {
    tags: ['spender', 'heal'],
    color: '#a3122a',
    animAs: B.mortalStrike,
    desc: 'Spend 50 Thirst: rip every Weeping Wound out of the target for {damage} Physical damage (+45% per wound) and heal yourself for 2% of your maximum health plus 20% of your missing health. The lower you are, the more you drink.',
    hint: 'Press 1 at 50 Thirst and 3 wounds: big damage and a big heal.',
  },
};

const gorecharge: AbilityDef = {
  id: BLOODREAVER.gorecharge,
  name: 'Gorecharge',
  school: 'physical',
  description: 'Charge an enemy up to 25 yd away, opening your own wound on the way: root it for 1 s, lose 5% health, gain Thirst.',
  targeting: 'enemy',
  range: 25,
  requiresFacing: true,
  requiresMobility: true,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  gain: [{ resource: 'thirst', amount: 10 }],
  effects: [
    { type: 'dash', mode: 'toTarget', distance: 25, speed: 44 },
    { type: 'damage', school: 'physical', amount: { coef: 0.9, variance: 0.05 } },
    wound(),
    { type: 'applyAura', aura: root(BLOODREAVER_AURA.goreRoot, 'Gored', 1000) },
    bleedSelf(0.05),
  ],
  ui: { tags: ['movement'], color: '#b0283c', animAs: B.charge, desc: 'Charge an enemy up to 25 yd away for {damage} Physical damage and a Weeping Wound; it is rooted for 1 sec. You lose 5% of your health and gain 10 Thirst (plus Thirst for the blood). Off the global cooldown.', hint: 'Press 2 to open: the first wound is yours.' },
};

const rendingArc: AbilityDef = {
  id: BLOODREAVER.rendingArc,
  name: 'Rending Arc',
  school: 'physical',
  description: 'A wide sweep that opens a Weeping Wound on every enemy within 8 yd. Costs 3% of your health, builds Thirst.',
  targeting: 'none',
  cooldown: { durationMs: 6000 },
  gain: [{ resource: 'thirst', amount: 8 }],
  effects: [
    { type: 'damage', school: 'physical', to: 'area', area: aroundSelf(8, 10), amount: { coef: 1.7, variance: 0.05 }, isAoE: true },
    { type: 'applyAura', to: 'area', area: aroundSelf(8, 10), aura: weepingWound },
    bleedSelf(0.03),
  ],
  ui: { tags: ['aoe', 'builder'], color: '#c0392b', animAs: B.cleave, desc: 'Sweep every enemy within 8 yd for {damage} Physical damage and open a Weeping Wound on each. You lose 3% of your health and gain 8 Thirst (plus Thirst for the blood).', hint: 'Press 3 in a pack: wounds on everything.' },
};

const bleedingEdge: AbilityDef = {
  id: BLOODREAVER.bleedingEdge,
  name: 'Bleeding Edge',
  school: 'physical',
  description: 'A cut that opens a Weeping Wound (up to 3). Costs 2% of your health, builds Thirst.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  gain: [{ resource: 'thirst', amount: 8 }],
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 2.4, variance: 0.05 } }, wound(), bleedSelf(0.02)],
  ui: { tags: ['builder'], color: '#c0392b', animAs: B.rend, desc: 'Cut for {damage} Physical damage and open a Weeping Wound (bleeds every 2 sec for 12 sec, stacks to 3). You lose 2% of your health and gain 8 Thirst (plus Thirst for the blood).', hint: 'Press 4 as the filler: build wounds and Thirst.' },
};

const finalDraught: AbilityDef = {
  id: BLOODREAVER.finalDraught,
  name: 'Final Draught',
  school: 'physical',
  description: 'Usable on an enemy below 35% health, or for 20 s after a kill: a finishing cut that heals 8% plus a fifth of your missing health.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  usableIf: { type: 'or', any: [hasFreshKill, { type: 'targetHpBelow', pct: 0.35 }] },
  gain: [{ resource: 'thirst', amount: 10 }],
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 3.6, variance: 0.05 } },
    { type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.08, pctSourceMissingHp: 0.2 } },
    { type: 'removeAura', to: 'self', auraId: FRESH_KILL },
  ],
  ui: { tags: ['execute', 'heal'], color: '#8e1b2b', animAs: B.execute, desc: 'Usable on an enemy below 35% health, or for 20 sec after an enemy you fought dies. A finishing cut for {damage} Physical damage that heals you for 8% of your maximum health plus 20% of your missing health.', hint: 'Key 5 lights up below 35% and after kills.' },
};

const crimsonKick: AbilityDef = {
  id: BLOODREAVER.crimsonKick,
  name: 'Crimson Kick',
  school: 'physical',
  description: 'Kick the target, interrupting its cast and locking that school for 3 s. Heals you 8% if the target is bleeding.',
  targeting: 'enemy',
  melee: true,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 0.6 } },
    interrupt(3000),
    { type: 'conditional', cond: { type: 'targetHasAura', auraId: BLOODREAVER_AURA.weepingWound, fromSelf: true }, then: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.08 } }] },
  ],
  ui: { tags: ['interrupt'], color: '#d9a066', animAs: B.pummel, hint: 'Press 6 while an enemy is casting.' },
};

const sanguineVigor: AbilityDef = {
  id: BLOODREAVER.sanguineVigor,
  name: 'Sanguine Vigor',
  school: 'physical',
  description: 'Drink deep: heal 10% of your maximum health plus 35% of your missing health, and take 20% less damage for 6 s.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 40_000 },
  effects: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.1, pctSourceMissingHp: 0.35 } }, dr(BLOODREAVER_AURA.sanguineVigor, 'Sanguine Vigor', 6000, -0.2)],
  ui: { tags: ['defensive', 'majorDefensive'], color: '#a3122a', animAs: B.dieByTheSword, hint: 'Press 7 when you dove too deep.' },
};

const bloodletting: AbilityDef = {
  id: BLOODREAVER.bloodletting,
  name: 'Bloodletting',
  school: 'physical',
  description: 'Slash your own arm: lose 15% of your health for a burst of Thirst, and your next Bloodwrench hits 30% harder. Off the global cooldown.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 12_000 },
  usableIf: { type: 'not', cond: { type: 'selfHpBelow', pct: 0.3 } },
  gain: [{ resource: 'thirst', amount: 15 }],
  effects: [bleedSelf(0.15), { type: 'applyAura', to: 'self', aura: openVein }],
  ui: { tags: ['cooldown'], color: '#e0283c', animAs: B.dieByTheSword, desc: 'Usable above 30% health. Lose 15% of your health on purpose: gain 15 Thirst (plus Thirst for the blood) and Open Vein, your next Bloodwrench within 10 sec deals 30% more damage. Off the global cooldown.', hint: 'Press Q when you are healthy: dive for Thirst, crit and haste.' },
};

const crimsonTrance: AbilityDef = {
  id: BLOODREAVER.crimsonTrance,
  name: 'Crimson Trance',
  school: 'physical',
  description: 'For 10 s your health cannot drop below 1, you deal 10% more damage and heal for 20% of all damage you deal.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 90_000 },
  effects: [{ type: 'applyAura', to: 'self', aura: crimsonTranceBuff }],
  ui: { tags: ['cooldown', 'majorDefensive'], color: '#ff2a4a', animAs: B.bladestorm, hint: 'Press E to dive as low as you dare: you cannot die for 10 s.' },
};

export const bloodreaver: SpecDef = {
  id: WARRIOR_SPEC.bloodreaver,
  baseClassId: 'warrior',
  name: 'Bloodreaver',
  role: 'dps',
  unlockLevel: 10,
  evolutions: {
    [WA.breaker]: BLOODREAVER.bloodwrench,
    [WA.headlong]: BLOODREAVER.gorecharge,
    [WA.wideArc]: BLOODREAVER.rendingArc,
    [WA.rive]: BLOODREAVER.bleedingEdge,
    [WA.bloodprize]: BLOODREAVER.finalDraught,
    [WA.shieldCheck]: BLOODREAVER.crimsonKick,
    [WA.ironBreath]: BLOODREAVER.sanguineVigor,
  },
  extraAbilities: [
    { key: 'Q', abilityId: BLOODREAVER.bloodletting },
    { key: 'E', abilityId: BLOODREAVER.crimsonTrance },
  ],
  abilities: [bloodwrench, gorecharge, rendingArc, bleedingEdge, finalDraught, crimsonKick, sanguineVigor, bloodletting, crimsonTrance],
  // Thirst: 1.5 per 1% of maximum health lost (self-harm included) and a little per swing; drains out of combat.
  resources: [{ id: 'thirst', name: 'Thirst', max: 100, start: 0, regenPerSecOoc: -10, oocSettle: 0, gainOn: [{ on: 'damageTaken', amount: 1.5, perPctMaxHp: true }, { on: 'meleeSwingLanded', amount: 4 }] }],
  passives: [redThirst],
  removePassives: [BASE_AURA.warriorThreat],
  auras: [weepingWound, openVein, crimsonTranceBuff],
  baseStats: DPS_STATS,
  maxHp: PLAYER_HP.melee,
  // One red-steel greatsword: slow, heavy swings.
  melee: { damage: { coef: 2.7, variance: 0.05 }, speedMs: 3400, school: 'physical' },
  color: '#a3122a',
  ui: {
    tagline: 'Cuts their own flesh to feed a greatsword that hits harder the closer they are to death.',
    fantasy: 'A red-steel greatsword that drinks. You choose to bleed: open your own veins to fuel it, dive to low health for monstrous crits, then drink it all back with Bloodwrench.',
    signature: 'Thirst: every 1% of health you lose (on purpose too) is 1.5 Thirst; missing health becomes up to +50% crit and +30% haste. Bloodwrench spends 50 and heals for a share of what is missing.',
    weapon: 'red_greatsword',
    tint: '#a3122a',
    glow: '#ff4a5c',
  },
};

/** The implemented Warrior specs (Tower Warden, harness, bots). */
export const WARRIOR_SPECS: SpecDef[] = [ironclad, ravager, bloodreaver];
/** Preview info for all three (the launch set is bold in advancement.md). */
export const WARRIOR_SPEC_INFO: SpecInfo[] = [infoOf(ironclad, true), infoOf(ravager, true), infoOf(bloodreaver, false)];
